import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { chatHaptic } from '../utils/chatHaptic';

interface PollCreatorProps {
  isOpen: boolean;
  onClose: () => void;
  onCreatePoll: (question: string, options: string[]) => void;
}

export function PollCreator({ isOpen, onClose, onCreatePoll }: PollCreatorProps) {
  const { t } = useTranslation();
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['', '']);
  const [errors, setErrors] = useState<string[]>([]);
  const questionInputRef = useRef<HTMLInputElement>(null);
  const optionRefs = useRef<(HTMLInputElement | null)[]>([]);

  const MAX_QUESTION_LENGTH = 200;
  const MIN_OPTIONS = 2;
  const MAX_OPTIONS = 4;

  useEffect(() => {
    if (isOpen && questionInputRef.current) {
      questionInputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const handleClose = () => {
    setQuestion('');
    setOptions(['', '']);
    setErrors([]);
    onClose();
  };

  const handleAddOption = () => {
    if (options.length < MAX_OPTIONS) {
      setOptions([...options, '']);
      setTimeout(() => {
        optionRefs.current[options.length]?.focus();
      }, 0);
    }
  };

  const handleRemoveOption = (index: number) => {
    if (options.length > MIN_OPTIONS) {
      const newOptions = options.filter((_, i) => i !== index);
      setOptions(newOptions);
    }
  };

  const handleOptionChange = (index: number, value: string) => {
    const newOptions = [...options];
    newOptions[index] = value;
    setOptions(newOptions);
  };

  const handleOptionKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (index === options.length - 1) {
        // Last option - try to submit
        handleSubmit();
      } else {
        // Focus next option
        optionRefs.current[index + 1]?.focus();
      }
    }
  };

  const validate = (): boolean => {
    const newErrors: string[] = [];

    if (!question.trim()) {
      newErrors.push(t('messaging.questionRequired'));
    }

    const filledOptions = options.filter(opt => opt.trim());
    if (filledOptions.length < MIN_OPTIONS) {
      newErrors.push(t('messaging.minOptionsRequired', { count: MIN_OPTIONS }));
    }

    const emptyOptions = options.some(opt => !opt.trim());
    if (emptyOptions) {
      newErrors.push(t('messaging.allOptionsMustBeFilled'));
    }

    const uniqueOptions = new Set(options.map(opt => opt.trim().toLowerCase()));
    if (uniqueOptions.size !== options.length) {
      newErrors.push(t('messaging.noDuplicateOptions'));
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      chatHaptic.pollVote();
      onCreatePoll(question.trim(), options.map(opt => opt.trim()));
      handleClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/50 z-40"
          />

          {/* Modal */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed bottom-0 left-0 right-0 z-50 bg-white/40 border-t border-white/20 rounded-t-3xl shadow-sm max-h-[80dvh] overflow-y-auto glass-surface pb-safe"
          >
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg sm:text-2xl font-semibold tracking-wide text-neutral-800">
                  {t('messaging.createPoll')}
                </h2>
                <button
                  onClick={handleClose}
                  className="p-2 bg-white/80 border border-white/20 rounded-lg hover:bg-neutral-50 transition-colors"
                  aria-label={t('messaging.close')}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M5 5L15 15M15 5L5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>

              {/* Question Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-neutral-800 mb-2">
                  {t('messaging.pollQuestion')}
                </label>
                <input
                  ref={questionInputRef}
                  type="text"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value.slice(0, MAX_QUESTION_LENGTH))}
                  placeholder={t('messaging.enterQuestion')}
                  className="w-full px-4 py-3 bg-white/80 border border-white/20 rounded-xl font-medium text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-slate-400"
                  maxLength={MAX_QUESTION_LENGTH}
                />
                <div className="text-xs text-neutral-500 mt-1 text-right font-medium">
                  {question.length}/{MAX_QUESTION_LENGTH}
                </div>
              </div>

              {/* Options */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-neutral-800 mb-2">
                  {t('messaging.pollOptions')}
                </label>
                <div className="space-y-3">
                  {options.map((option, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-slate-800 text-white border border-white/20 rounded-full font-medium">
                        {index + 1}
                      </div>
                      <input
                        ref={(el) => { optionRefs.current[index] = el; }}
                        type="text"
                        value={option}
                        onChange={(e) => handleOptionChange(index, e.target.value)}
                        onKeyDown={(e) => handleOptionKeyDown(index, e)}
                        placeholder={t('messaging.enterOption', { number: index + 1 })}
                        className="flex-1 px-4 py-3 bg-white/80 border border-white/20 rounded-xl font-medium text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-slate-400"
                      />
                      {options.length > MIN_OPTIONS && (
                        <button
                          onClick={() => handleRemoveOption(index)}
                          className="flex-shrink-0 p-2 bg-white/80 border border-white/20 rounded-lg hover:bg-error-500 hover:border-error-500 hover:text-white transition-colors"
                          aria-label={t('messaging.removeOption')}
                        >
                          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M5 10H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                          </svg>
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                {/* Add Option Button */}
                {options.length < MAX_OPTIONS && (
                  <button
                    onClick={handleAddOption}
                    className="mt-3 w-full px-4 py-3 bg-white/80 border border-white/20 rounded-xl font-medium text-neutral-800 hover:bg-neutral-50 transition-colors flex items-center justify-center gap-2"
                  >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M10 5V15M5 10H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    {t('messaging.addOption')} ({options.length}/{MAX_OPTIONS})
                  </button>
                )}
              </div>

              {/* Errors */}
              {errors.length > 0 && (
                <div className="mb-6 p-4 bg-error-50 border border-error-300 rounded-xl">
                  {errors.map((error, index) => (
                    <div key={index} className="text-sm font-medium text-error-700 flex items-start gap-2">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="flex-shrink-0 mt-0.5">
                        <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2"/>
                        <path d="M8 5V8M8 11V11.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                      {error}
                    </div>
                  ))}
                </div>
              )}

              {/* Create Poll Button */}
              <button
                onClick={handleSubmit}
                className="w-full px-6 py-4 bg-slate-700 border border-slate-400 rounded-xl font-medium text-white transition-all active:scale-[0.98]"
              >
                {t('messaging.createPoll')}
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
